import React from "react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import { lightTheme } from "../../styles/theme";

export interface CirclePickerOption {
  circleId: string;
  name: string;
  logo?: string;
  isPublic?: boolean;
  memberCount?: number;
}

interface CirclePickerGridProps {
  circles: CirclePickerOption[];
  selectedCircleIds: string[];
  onToggle: (circleId: string) => void;
  isLoading?: boolean;
  title?: string;
  helperText?: string;
  compact?: boolean;
  /** Styled for use inside AddUserFromSearchEmptyState and similar panels. */
  embedded?: boolean;
}

const getCircleLogoSrc = (logo?: string): string | undefined =>
  logo && logo.trim() !== "" && !logo.includes("placehold.co") ? logo : undefined;

const CirclePickerGrid: React.FC<CirclePickerGridProps> = ({
  circles,
  selectedCircleIds,
  onToggle,
  isLoading = false,
  title = "Add to circles (optional)",
  helperText,
  compact = false,
  embedded = false,
}) => {
  const theme = useTheme();
  const isCompactLayout = compact || embedded;

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Loading circles...
      </Typography>
    );
  }

  if (circles.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: isCompactLayout && !embedded ? 2 : 0,
        textAlign: embedded ? "left" : isCompactLayout ? "left" : "left",
        maxWidth: embedded ? "none" : isCompactLayout ? 520 : "none",
        mx: isCompactLayout && !embedded ? "auto" : 0,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          mb: helperText ? 0.5 : 1.25,
          textAlign: embedded ? "left" : isCompactLayout ? "center" : "left",
          fontSize: embedded ? "0.875rem" : undefined,
        }}
      >
        {title}
        {!embedded ? " (optional)" : null}
      </Typography>
      {helperText ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.75,
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            textAlign: embedded ? "left" : isCompactLayout ? "center" : "left",
          }}
        >
          {helperText}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(3, minmax(0, 1fr))",
            sm: embedded
              ? "repeat(4, minmax(0, 1fr))"
              : isCompactLayout
                ? "repeat(3, minmax(0, 1fr))"
                : "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 1, sm: 1.1 },
        }}
      >
        {circles.map((circle) => {
          const isSelected = selectedCircleIds.includes(circle.circleId);
          const logoSrc = getCircleLogoSrc(circle.logo);

          return (
            <Paper
              key={circle.circleId}
              elevation={0}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => onToggle(circle.circleId)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onToggle(circle.circleId);
                }
              }}
              sx={{
                position: "relative",
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "divider",
                borderRadius: 1.5,
                p: { xs: 0.65, sm: 0.75 },
                cursor: "pointer",
                bgcolor: isSelected
                  ? alpha(theme.palette.primary.main, 0.06)
                  : "background.paper",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {isSelected ? (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    fontSize: 15,
                    color: "primary.main",
                  }}
                />
              ) : null}
              <Box
                sx={{
                  width: { xs: 34, sm: 36 },
                  height: { xs: 34, sm: 36 },
                  borderRadius: "50%",
                  mx: "auto",
                  mb: 0.5,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: logoSrc
                    ? "background.paper"
                    : getAvatarColor(circle.name, lightTheme),
                  color: "common.white",
                }}
              >
                {logoSrc ? (
                  <Box
                    component="img"
                    src={logoSrc}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                  >
                    {getInitials(circle.name)}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: "0.72rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {circle.name}
              </Typography>
              {!isCompactLayout && circle.memberCount !== undefined ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 0.15,
                    fontSize: "0.62rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {circle.isPublic ? "Public" : "Private"} • {circle.memberCount}{" "}
                  members
                </Typography>
              ) : null}
            </Paper>
          );
        })}
      </Box>
      {selectedCircleIds.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            mt: 1.25,
            justifyContent: embedded ? "flex-start" : isCompactLayout ? "center" : "flex-start",
          }}
        >
          {selectedCircleIds.map((circleId) => {
            const circle = circles.find((item) => item.circleId === circleId);
            return (
              <Chip
                key={circleId}
                label={circle?.name || circleId}
                size="small"
                onDelete={() => onToggle(circleId)}
                sx={{
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.dark",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                }}
              />
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

export default CirclePickerGrid;
